<template>
    <div class="block xl:flex flex-row gap-x-6 xl:w-3/5 xl:pr-4">
        <!-- Column 1 -->
        <div class="xl:w-1/2">
            <div class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="name" value="Name" class=""/>
                    <jet-input type="text" class="block w-full" v-model="form.name" ref="name" />
                    <jet-input-error :message="$page.props.errors.name" class="mt-1" />
                </div>

                <div v-if="showEnabledToggle">
                    <toggle id="enabled" class="py-1" v-model="form.enabled" />
                    <jet-label for="enabled" value="Active" class="ml-2 inline-block" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="address_1" value="Address 1"/>
                <jet-input type="text" class="block w-full" v-model="form.address_1" />
                <jet-input-error :message="$page.props.errors.address_1" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="address_2" value="Address 2"/>
                <jet-input type="text" class="block w-full" v-model="form.address_2" />
                <jet-input-error :message="$page.props.errors.description" class="mt-1" />
            </div>

            <div class="flex gap-x-4 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="city" value="City"/>
                    <jet-input type="text" class="block w-full" v-model="form.city" />
                    <jet-input-error :message="$page.props.errors.city" class="mt-1" />
                </div>

                <div class="flex-1">
                    <jet-label for="postal_code" value="Postal Code" class="whitespace-nowrap"/>
                    <jet-input type="text" class="block w-full" v-model="form.postal_code" />
                    <jet-input-error :message="$page.props.errors.postal_code" class="mt-1" />
                </div>

                <div class="flex-1">
                    <jet-label for="province" value="Province"/>
                    <multiselect class="multi-lg"
                        v-model="form.province"
                        :options="provinceOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                    />
                    <jet-input-error :message="$page.props.errors.province" class="mt-1" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="po_num" value="PO #"/>
                <jet-input type="text" class="block w-full" v-model="form.po_num" />
                <jet-input-error :message="$page.props.errors.po_num" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="phone" value="Phone"/>
                <jet-input type="tel" class="block w-full" v-model="form.phone" />
                <jet-input-error :message="$page.props.errors.description" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="cell" value="Cell"/>
                <jet-input type="tel" class="block w-full" v-model="form.cell" />
                <jet-input-error :message="$page.props.errors.cell" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="fax" value="Fax"/>
                <jet-input type="tel" class="block w-full" v-model="form.fax" />
                <jet-input-error :message="$page.props.errors.fax" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="email" value="General Notification Emails"/>
                <jet-input type="text" class="block w-full" v-model="form.email" />
                <span class="text-xs text-gray-400 leading-tight">Email addresses to send all notifications to. Separate multiple email addresses with a comma.</span>
                <jet-input-error :message="$page.props.errors.email" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="invoice_emails" value="Invoice Emails"/>
                <jet-input type="text" class="block w-full" v-model="form.invoice_emails" />
                <span class="text-xs text-gray-400 leading-tight">Additional email addresses to send only invoices to. Separate multiple email addresses with a comma.</span>
                <jet-input-error :message="$page.props.errors.invoice_emails" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="ap_email" value="AP Email"/>
                <jet-input type="email" class="block w-full" v-model="form.ap_email" />
                <jet-input-error :message="$page.props.errors.ap_email" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="contact_name" value="Contact Name"/>
                <jet-input type="text" class="block w-full" v-model="form.contact_name" />
                <jet-input-error :message="$page.props.errors.contact_name" class="mt-1" />
            </div>

            <div class="mb-4">
                <toggle id="pallet_orders" class="py-1" v-model="form.pallet_orders" />
                <jet-label for="pallet_orders" value="Pallet Orders" class="ml-2 inline-block" />
            </div>

            <div class="mb-4">
                <toggle id="requires_printed_order" class="py-1" v-model="form.requires_printed_order" />
                <jet-label for="requires_printed_order" value="Requires Printed Order" class="ml-2 inline-block" />
                <div class="text-xs text-gray-400 leading-tight">Prints a paper copy of the order (not the invoice) with the delivery.</div>
            </div>

            <div class="mb-4">
                <toggle id="show_printed_order_pricing" class="py-1" v-model="form.show_printed_order_pricing" />
                <jet-label for="show_printed_order_pricing" value="Printed Orders Show Pricing" class="ml-2 inline-block" />
            </div>
        </div>

        <!-- Column 2 -->
        <div class="xl:w-1/2 flex-1 gap-y-4">
            <div class="mb-4">
                <jet-label for="type" value="Type" />
                <multiselect
                    v-model="form.type"
                    :options="typeOptions"
                    :classes="{ singleLabel: 'w-full px-2' }"
                    class="multi-lg"
                    placeholder="Select type"
                />
                <jet-input-error :message="$page.props.errors.type" class="mt-1" />
            </div>

            <div class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="pricing_level" value="Pricing Level" />
                    <multiselect
                        v-model="form.pricing_level_id"
                        :options="pricingOptions"
                        placeholder="Select level"
                        :classes="{singleLabel: 'w-full px-2'}"
                        class="multi-lg"
                    />
                    <jet-input-error :message="$page.props.errors.pricing_level_id" class="mt-1" />
                </div>
                <div>
                    <toggle id="tax_exempt" class="py-1" v-model="form.tax_exempt" />
                    <jet-label for="tax_exempt" value="Tax Exempt" class="ml-2 inline-block" />
                </div>
            </div>

            <div v-if="isAdmin && form.type === 'School'" class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="credit_balance" value="Prepaid & Fundraising Balance" />
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="number" min="0" step="0.01" v-numbers v-model="form.credit_balance" class="w-full rounded-l-none"></jet-input>
                    </div>
                    <jet-input-error :message="$page.props.errors.credit_balance" class="mt-1" />
                </div>
                <div>
                    <toggle id="auto_apply_credit" class="py-1" v-model="form.auto_apply_credit" />
                    <jet-label for="auto_apply_credit" value="Auto Apply Balance" class="ml-2 inline-block" />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="payment_terms" value="Payment Terms" class="whitespace-nowrap"/>
                    <multiselect
                        v-model="form.payment_terms"
                        :options="paymentTermsOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :canClear="false"
                        :canDeselect="false"
                        placeholder="Select terms"
                        class="multi-lg"
                    />
                </div>

                 <div class="flex-1">
                    <jet-label for="payment_method" value="Payment Method" class="whitespace-nowrap"/>
                    <multiselect
                    v-model="form.payment_method"
                    :options="paymentMethodOptions"
                    :classes="{singleLabel: 'w-full px-2'}"
                    :canClear="false"
                    :canDeselect="false"
                    placeholder="Select method"
                    class="multi-lg"
                    />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="default_shipping_method" value="Default Shipping Method" class="whitespace-nowrap"/>
                    <multiselect
                        v-model="form.default_shipping_method"
                        :options="shippingOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :canClear="false"
                        :canDeselect="false"
                        class="multi-lg"
                    />
                </div>

                 <div class="flex-1">
                    <jet-label for="route" value="Routific Route" />
                    <multiselect
                        v-model="form.route"
                        :options="routeOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        class="multi-lg"
                        placeholder="Select route"
                    />
                    <jet-input-error :message="$page.props.errors.route" class="mt-1" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="delivery_duration" value="Delivery Duration (minutes)" />
                <jet-input type="number" v-model="form.delivery_duration" class="w-full" :disabled="deliveryLogisticsFieldsDisabled" min="1"></jet-input>
                <jet-input-error :message="$page.props.errors.delivery_duration" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="delivery_fee_override" value="Delivery Fee Override"/>
                <div class="flex">
                    <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                    <jet-input type="text" class="w-full rounded-l-none" v-model="form.delivery_fee_override" id="delivery_fee_override"/>
                </div>
                <div class="text-xs text-gray-500">Fixed delivery charge that always applies regardless of order value.</div>
                <jet-input-error :message="$page.props.errors.delivery_fee_override" class="mt-1" />
            </div>

            <template v-if="!hasDeliveryFeeOverride">
                <div class="mb-4">
                    <jet-label for="delivery_order_minimum_override" value="Delivery Order Minimum"/>
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="text" class="w-full rounded-l-none" v-model="form.delivery_order_minimum_override" id="delivery_order_minimum_override"/>
                    </div>
                    <div class="text-xs text-gray-500">Leave blank to use the wholesale default delivery order minimum.</div>
                    <jet-input-error :message="$page.props.errors.delivery_order_minimum_override" class="mt-1" />
                </div>

                <div class="mb-4">
                    <jet-label for="delivery_fee_below_minimum_override" value="Delivery Fee Below Minimum"/>
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="text" class="w-full rounded-l-none" v-model="form.delivery_fee_below_minimum_override" id="delivery_fee_below_minimum_override"/>
                    </div>
                    <div class="text-xs text-gray-500">Fee when order is below the delivery minimum. Leave blank to use the wholesale default.</div>
                    <jet-input-error :message="$page.props.errors.delivery_fee_below_minimum_override" class="mt-1" />
                </div>
            </template>

            <div class="mb-4">
                <jet-label for="sales_rep_id" value="Sales Rep:"/>
                <multiselect
                    v-model="form.sales_rep_id"
                    valueProp="id"
                    label="name"
                    :options="salesReps"
                    :classes="{ singleLabel: 'w-full px-2' }"
                    class="multi-lg"
                />
                <jet-input-error :message="$page.props.errors.sales_rep_id" class="mt-1" />
            </div>

            <div class="mb-4">
                <toggle id="send_inactive_alerts" class="py-1" v-model="form.send_inactive_alerts" />
                <jet-label for="send_inactive_alerts" value="Send Inactive Alerts" class="ml-2 inline-block" />
                <jet-input-error :message="$page.props.errors.send_inactive_alerts" class="mt-1" />
            </div>

            <div v-if="form.send_inactive_alerts" class="mb-4">
                <div class="flex items-center gap-2">
                    <jet-label for="inactive_alert_weeks" value="Inactive Alert Weeks" />

                    <BetterTooltip>
                        <template #reference>
                            <div class="text-xs text-gray-400 cursor-pointer pb-2">
                                <i class="fas fa-info-circle text-xs"></i>
                            </div>
                        </template>
                        <template #floating>
                            Number of weeks without an order before an alert is sent (must be an integer).
                        </template>
                    </BetterTooltip>
                </div>

                <div class="flex w-full">
                    <jet-input id="inactive_alert_weeks" type="number" min="1" class="w-full rounded-r-none" v-model="form.inactive_alert_weeks"
                        :disabled="!form.send_inactive_alerts"
                    />
                    <span class="pt-1 px-3 border border-l-0 rounded-r text-sm flex items-center">Weeks</span>
                </div>

                <jet-input-error :message="$page.props.errors.inactive_alert_weeks" class="mt-1" />
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="skills" value="Skills" />
                    <multiselect
                        v-model="form.skills"
                        :options="skillOptions"
                        mode="tags"
                        :createTag="true"
                        :closeOnSelect="false"
                        placeholder="e.g. SCH, WHO, Retail, EVE"
                        :classes="{multipleLabel: 'w-full px-2'}"
                        class="multi-lg truncate-placeholder"
                    />
                    <div class="text-xs text-gray-500">Copied onto new orders. Codes must also exist in OptimoRoute (Administration → Skills). Editing here does not change existing orders.</div>
                    <jet-input-error :message="$page.props.errors.skills" class="mt-1" />
                </div>
                <div class="flex-1">
                    <jet-label for="vehicle_features" value="Vehicle Type" />
                    <multiselect
                        v-model="form.vehicle_features"
                        :options="vehicleFeatureOptions"
                        mode="tags"
                        :createTag="true"
                        :closeOnSelect="false"
                        placeholder="e.g. BOXTRUCK"
                        :classes="{multipleLabel: 'w-full px-2'}"
                        class="multi-lg truncate-placeholder"
                    />
                    <div class="text-xs text-gray-500">Sent as OptimoRoute vehicle features. Codes must also exist in OptimoRoute (Administration → Vehicle features).</div>
                    <jet-input-error :message="$page.props.errors.vehicle_features" class="mt-1" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="priority" value="Routing Priority (default)" />
                <multiselect
                    v-model="form.priority"
                    :options="priorityOptions"
                    :canClear="false"
                    :disabled="true"
                    :classes="{singleLabel: 'w-full px-2'}"
                    class="multi-lg"
                    placeholder="Select priority"
                />
                <jet-input-error :message="$page.props.errors.priority" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="order_notes">
                    OptimoRoute Driver Notes<span v-if="optimorouteNotesRequired" class="text-red-600"> *</span>
                </jet-label>
                <textarea
                    id="order_notes"
                    class="block w-full resize-none"
                    rows="4"
                    maxlength="1000"
                    v-model="form.order_notes"
                    :required="optimorouteNotesRequired"
                />
                <jet-input-error :message="$page.props.errors.order_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="internal_notes" value="Internal Notes"/>
                <textarea class="block w-full resize-none" rows="4" v-model="form.internal_notes" />
                <jet-input-error :message="$page.props.errors.internal_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="load_notes" value="Load Sheet Notes"/>
                <textarea class="block w-full resize-none" rows="4" v-model="form.load_notes" :disabled="deliveryLogisticsFieldsDisabled" />
                <jet-input-error :message="$page.props.errors.load_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="picking_notes" value="Picking Notes"/>
                <textarea class="block w-full resize-none" rows="3" v-model="form.picking_notes" />
                <jet-input-error :message="$page.props.errors.picking_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <toggle id="picking_red_flag" class="py-1" v-model="form.picking_red_flag" />
                <jet-label for="picking_red_flag" value="Red flag for next pick" class="ml-2 inline-block" />
                <span class="text-xs text-gray-400 leading-tight block mt-1">When on, the next order will show a quality warning during picking. Cleared automatically after that order is picked.</span>
                <jet-input-error :message="$page.props.errors.picking_red_flag" class="mt-1" />
            </div>
        </div>
    </div>
</template>

<script>
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import BetterTooltip from '@/Jetstream/BetterTooltip';

export default {
    props: [
        'form', 
        'salesReps', 
        'typeOptions', 
        'routeOptions', 
        'pricingOptions', 
        'paymentTermsOptions',
        'paymentMethodOptions',
        'showEnabledToggle'
    ],

    components: {
        JetLabel,
        JetInput,
        JetInputError,
        Multiselect,
        Toggle,
        BetterTooltip,
    },

    setup () {
        const page = usePage();
        const { isLogistics, isSales, isAdmin, isCustomer } = UseUserAuth(page.props.value.user);

        return {
            isLogistics,
            isSales,
            isAdmin,
            isCustomer,
            page,
        }
        
    },

    data() {
        return {
            provinceOptions: [
                'Ontario',
                'Manitoba',
                'Saskatchewan',
                'British Columbia',
                'Yukon',
                'Northwest Territories',
                'Nunavut',
                'Quebec',
                'Nova Scotia',
                'New Brunswick',
                'Newfoundland',
                'Prince Edward Island'
            ],

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            priorityOptions: [
                { value: 'L', label: 'L — Low' },
                { value: 'M', label: 'M — Medium' },
                { value: 'H', label: 'H — High' },
                { value: 'C', label: 'C — Critical' },
            ],
        }
    },

    computed: {
        skillOptions() {
            return this.page.props.value.optimorouteSkillOptions || ['SCH', 'WHO', 'Retail', 'EVE'];
        },
        vehicleFeatureOptions() {
            return this.page.props.value.optimorouteVehicleTypeOptions || ['BOXTRUCK'];
        },
        hasDeliveryFeeOverride() {
            const v = parseFloat(this.form.delivery_fee_override);
            return !Number.isNaN(v) && v > 0;
        },
        deliveryLogisticsFieldsDisabled() {
            return this.isSales && !this.isLogistics && this.form.id !== null;
        },
        optimorouteNotesRequired() {
            return this.form.id === null;
        },
    },

    watch: {
        'form.delivery_fee_override'(val) {
            const v = parseFloat(val);
            if (!Number.isNaN(v) && v > 0) {
                this.form.delivery_order_minimum_override = null;
                this.form.delivery_fee_below_minimum_override = null;
            }
        },
    },

    mounted () {
        if (this.form.id === null) {
            this.$refs.name.focus();
        }
    },
}
</script>

<style scoped>
:deep(.truncate-placeholder .multiselect-placeholder) {
    min-width: 0;
    /* Reserve space for the dropdown caret. */
    max-width: calc(100% - 1.3rem);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>
